import { RefreshCw, Send } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Card,
  ChipToggle,
  DataTable,
  EmptyState,
  ErrorState,
  LoadingState,
  useToast,
} from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import { formatRM } from '@/features/billing/money';
import { AgeingIndicator } from '@/features/panel-receivables/AgeingIndicator';
import {
  derivePanelReceivableState,
  PANEL_RECEIVABLE_STATES,
  useAutoMatchPanelReceivable,
  usePanelReceivableBatches,
  useSendPanelReceivableBatch,
  type PanelReceivableBatch,
  type PanelReceivableState,
} from '@/features/panel-receivables/api';
import { BatchPatientDetailDrawer } from '@/features/panel-receivables/BatchPatientDetailDrawer';
import { PageContainer } from '@/layouts/PageContainer';
import { isApiError } from '@/lib/api';
import { useOptionalUrlState, useUrlState } from '@/lib/url-state';
import styles from './PanelReceivablesPage.module.css';

type View = 'board' | 'list';
type StagedBatch = PanelReceivableBatch & { stage: PanelReceivableState };

function BatchActions({
  batch,
  canManage,
  pending,
  onOpen,
  onSend,
  onMatch,
}: {
  batch: StagedBatch;
  canManage: boolean;
  pending: boolean;
  onOpen: () => void;
  onSend: () => void;
  onMatch: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className={styles.actions}>
      <Button size="sm" onClick={onOpen}>
        {t('panelReceivables.viewBatch')}
      </Button>
      {canManage && batch.stage === 'send' && (
        <Button
          size="sm"
          variant="primary"
          icon={<Send size={14} />}
          loading={pending}
          onClick={onSend}
        >
          {t('panelReceivables.sendBatch')}
        </Button>
      )}
      {canManage && batch.stage === 'match' && (
        <Button
          size="sm"
          variant="primary"
          icon={<RefreshCw size={14} />}
          loading={pending}
          onClick={onMatch}
        >
          {t('panelReceivables.runAutoMatch')}
        </Button>
      )}
    </div>
  );
}

export function PanelReceivablesPage() {
  const { t } = useTranslation();
  const { hasPermission } = useSession();
  const toast = useToast();
  const batches = usePanelReceivableBatches();
  const sendBatch = useSendPanelReceivableBatch();
  const autoMatch = useAutoMatchPanelReceivable();
  const [view, setView] = useUrlState<View>('view', 'board', ['board', 'list']);
  const [selectedId, setSelectedId] = useOptionalUrlState('drawer');
  const canManage = hasPermission('claims:manage');

  const send = (batch: PanelReceivableBatch) => {
    sendBatch.mutate(batch.id, {
      onSuccess: () =>
        toast.show({
          tone: 'success',
          title: t('panelReceivables.batchSent'),
        }),
    });
  };

  const match = (batch: PanelReceivableBatch) => {
    const remittanceIds = batch.remittances
      .filter((remittance) => remittance.status !== 'applied')
      .map((remittance) => remittance.id);
    if (remittanceIds.length === 0) return;
    autoMatch.mutate(
      { batchId: batch.id, remittanceIds },
      {
        onSuccess: () => {
          toast.show({
            tone: 'success',
            title: t('panelReceivables.autoMatched'),
          });
          setSelectedId(batch.id);
        },
      },
    );
  };

  const operationError = sendBatch.error ?? autoMatch.error;
  const apiError = isApiError(operationError) ? operationError : undefined;
  const rows: StagedBatch[] = (batches.data ?? []).map((batch) => ({
    ...batch,
    stage: derivePanelReceivableState(batch),
  }));

  const actions = (batch: StagedBatch) => (
    <BatchActions
      batch={batch}
      canManage={canManage}
      pending={
        (sendBatch.isPending && sendBatch.variables === batch.id) ||
        (autoMatch.isPending && autoMatch.variables?.batchId === batch.id)
      }
      onOpen={() => setSelectedId(batch.id)}
      onSend={() => send(batch)}
      onMatch={() => match(batch)}
    />
  );

  return (
    <PageContainer
      actions={
        <ChipToggle<View>
          label={t('panelReceivables.view')}
          single
          selected={[view]}
          onChange={(next) => next[0] && setView(next[0])}
          options={[
            { value: 'board', label: t('panelReceivables.board') },
            { value: 'list', label: t('panelReceivables.list') },
          ]}
        />
      }
    >
      <Card title={t('panelReceivables.title')}>
        <p className={styles.intro}>{t('panelReceivables.intro')}</p>
      </Card>

      {batches.isLoading && <LoadingState />}
      {batches.error && (
        <ErrorState
          error={batches.error}
          onRetry={() => void batches.refetch()}
        />
      )}
      {!batches.isLoading && !batches.error && rows.length === 0 && (
        <EmptyState
          title={t('panelReceivables.noBatches')}
          body={t('panelReceivables.noBatchesBody')}
        />
      )}

      {!batches.isLoading &&
        !batches.error &&
        rows.length > 0 &&
        view === 'board' && (
          <div
            className={styles.board}
            aria-label={t('panelReceivables.workflow')}
          >
            {PANEL_RECEIVABLE_STATES.map((stage) => {
              const stageRows = rows.filter((batch) => batch.stage === stage);
              return (
                <section className={styles.column} key={stage}>
                  <header className={styles.columnHeader}>
                    <h2>{t(`panelReceivables.states.${stage}`)}</h2>
                    <Badge>{stageRows.length}</Badge>
                  </header>
                  <div className={styles.columnBody}>
                    {stageRows.length === 0 && (
                      <p className={styles.emptyColumn}>
                        {t('panelReceivables.noBatchesInState')}
                      </p>
                    )}
                    {stageRows.map((batch) => (
                      <article className={styles.batchCard} key={batch.id}>
                        <div className={styles.batchHeading}>
                          <strong>{batch.panelName}</strong>
                          <span>{batch.reference}</span>
                        </div>
                        <dl className={styles.batchFacts}>
                          <div>
                            <dt>{t('panelReceivables.outstanding')}</dt>
                            <dd>{formatRM(batch.outstandingTotal)}</dd>
                          </div>
                          <div>
                            <dt>{t('panelReceivables.patients')}</dt>
                            <dd>{batch.patientCount}</dd>
                          </div>
                        </dl>
                        <AgeingIndicator
                          bucket={batch.ageingBucket}
                          days={batch.ageingDays}
                        />
                        {actions(batch)}
                      </article>
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        )}

      {!batches.isLoading &&
        !batches.error &&
        rows.length > 0 &&
        view === 'list' && (
          <Card title={t('panelReceivables.list')} flush>
            <DataTable
              caption={t('panelReceivables.title')}
              rows={rows}
              rowKey={(batch) => batch.id}
              columns={[
                {
                  key: 'batch',
                  header: t('panelReceivables.batch'),
                  render: (batch) => (
                    <div>
                      <strong>{batch.panelName}</strong>
                      <span className={styles.muted}>{batch.reference}</span>
                    </div>
                  ),
                },
                {
                  key: 'state',
                  header: t('panelReceivables.state'),
                  render: (batch) => (
                    <Badge tone={batch.stage === 'closed' ? 'success' : 'info'}>
                      {t(`panelReceivables.states.${batch.stage}`)}
                    </Badge>
                  ),
                },
                {
                  key: 'patients',
                  header: t('panelReceivables.patients'),
                  render: (batch) => batch.patientCount,
                },
                {
                  key: 'outstanding',
                  header: t('panelReceivables.outstanding'),
                  align: 'right',
                  render: (batch) => formatRM(batch.outstandingTotal),
                },
                {
                  key: 'ageing',
                  header: t('panelReceivables.ageingLabel'),
                  render: (batch) => (
                    <AgeingIndicator
                      bucket={batch.ageingBucket}
                      days={batch.ageingDays}
                    />
                  ),
                },
                {
                  key: 'actions',
                  header: t('panelReceivables.actionsLabel'),
                  render: actions,
                },
              ]}
            />
          </Card>
        )}

      {apiError && (
        <ErrorState
          error={apiError}
          onRetry={() => {
            sendBatch.reset();
            autoMatch.reset();
            void batches.refetch();
          }}
        />
      )}

      {selectedId && (
        <BatchPatientDetailDrawer
          batchId={selectedId}
          canManage={canManage}
          onClose={() => setSelectedId(null)}
        />
      )}
    </PageContainer>
  );
}
